import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  currency,
  DEFAULT_VALUES,
  marginTone,
  totalQty,
  UiSchema,
  type UiValues,
} from './screen-print-quote/_shared';
import { loadPinned, type PinnedScenario } from './screen-print-quote/calc-pinned';
import { ComparisonBar } from './screen-print-quote/comparison';
import { JobNameInput } from './screen-print-quote/job-name-input';
import { ParseTextbox } from './screen-print-quote/parse-textbox';
import { QuoteForm } from './screen-print-quote/quote-form';
import { QuoteResultPanel } from './screen-print-quote/quote-result-panel';
import { ScenarioTabs } from './screen-print-quote/scenario-tabs';
import { useCalcShortcuts } from './screen-print-quote/use-calc-shortcuts';
import { useLiveQuote } from './screen-print-quote/use-live-quote';

// Re-export for existing test imports at `@/routes/screen-print-quote`.
export {
  type ApiErrorBody,
  DEFAULT_VALUES,
  formatError,
  type QuoteError,
  type ScreenPrintRequestBody,
  toApiInput,
  UiSchema,
  type UiValues,
} from './screen-print-quote/_shared';

export function ScreenPrintQuotePage() {
  const form = useForm<UiValues>({
    resolver: zodResolver(UiSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });
  const values = useWatch({ control: form.control }) as UiValues;
  const liveQuote = useLiveQuote(values);
  useCalcShortcuts(form);
  const [pinned, setPinned] = useState<PinnedScenario[]>([]);
  // Proposal C: which scenario tab is active. `null` = Live (form's
  // current state). Any other value = the id of a pinned scenario.
  // Clicking a pin tab resets the form to that pin's values; this
  // state drives the tab highlight + remove UX.
  const [activePinId, setActivePinId] = useState<string | null>(null);

  // Hydrate pins from localStorage on first render. Kept in an effect
  // so SSR / tests don't touch window synchronously.
  useEffect(() => {
    setPinned(loadPinned());
  }, []);

  const liveResult = liveQuote.status === 'ready' ? liveQuote.result : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-5xl px-4 py-6 lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8 lg:px-6 lg:py-8">
        <div className="min-w-0 space-y-6">
          <JobNameInput form={form} />
          <ParseTextbox form={form} />
          <ScenarioTabs
            form={form}
            liveValues={values}
            liveResult={liveResult}
            pinned={pinned}
            activePinId={activePinId}
            onActivePin={setActivePinId}
            onChange={setPinned}
          />
          <QuoteForm form={form} />
        </div>

        {/* Desktop + tablet preview + pinned bar beneath it */}
        <aside className="mt-10 lg:mt-0 lg:sticky lg:top-20 lg:self-start">
          <QuoteResultPanel
            state={liveQuote}
            values={values}
            form={form}
            onPinnedChange={setPinned}
          />
          <ComparisonBar
            form={form}
            liveValues={values}
            liveResult={liveResult}
            pinned={pinned}
            onChange={setPinned}
          />
        </aside>
      </div>

      {/* Mobile sticky bar (only <md). Tap opens a bottom Sheet with the same panel. */}
      <MobileSummaryBar
        state={liveQuote}
        values={values}
        form={form}
        pinned={pinned}
        setPinned={setPinned}
        liveResult={liveResult}
      />
    </div>
  );
}

function MobileSummaryBar({
  state,
  values,
  form,
  pinned,
  setPinned,
  liveResult,
}: {
  state: ReturnType<typeof useLiveQuote>;
  values: UiValues;
  form: ReturnType<typeof useForm<UiValues>>;
  pinned: PinnedScenario[];
  setPinned: (list: PinnedScenario[]) => void;
  liveResult: Parameters<typeof ComparisonBar>[0]['liveResult'];
}) {
  const [open, setOpen] = useState(false);
  const summary = summaryText(state, values);

  return (
    <>
      {/* Spacer so the sticky bar doesn't hide the last form row on scroll. */}
      <div className="h-[72px] md:hidden" aria-hidden />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-lg backdrop-blur md:hidden"
            aria-label="Show full quote details"
          >
            <div className="flex min-w-0 flex-col text-left">
              <span className="truncate text-base font-semibold tabular-nums">
                {summary.primary}
              </span>
              <span className="truncate text-xs text-muted-foreground tabular-nums">
                {summary.secondary}
              </span>
            </div>
            <span className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
              Details
            </span>
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="max-h-[90vh] overflow-y-auto rounded-t-2xl p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <SheetTitle className="sr-only">Quote details</SheetTitle>
          {/* Drag-handle pill — standard iOS/Android bottom-sheet
              affordance. Tapping it closes the sheet as a fallback
              for users who don't discover the drag gesture. Replaces
              the outlined X in the corner. */}
          <SheetClose asChild>
            <button
              type="button"
              aria-label="Close details"
              className="mx-auto mb-2 block h-1.5 w-12 rounded-full bg-muted-foreground/40 transition-colors hover:bg-muted-foreground/70"
            />
          </SheetClose>
          <div className="space-y-3">
            <QuoteResultPanel
              state={state}
              values={values}
              form={form}
              onPinnedChange={setPinned}
            />
            <ComparisonBar
              form={form}
              liveValues={values}
              liveResult={liveResult}
              pinned={pinned}
              onChange={setPinned}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/**
 * Condensed ready-state summary for the mobile sticky bar. Matches the
 * Claude Design mock's "$6.98/unit · 48 × = $335.04 · Healthy 37% ·
 * Floor safe" treatment — every metric the owner scans before deciding to
 * expand the sheet.
 *
 * Floor state mirrors the desktop FloorChip escalation so the mobile
 * bar visibly changes when a quote drifts toward (or below) the floor.
 */
function summaryText(
  state: ReturnType<typeof useLiveQuote>,
  values: UiValues,
): { primary: string; secondary: string } {
  if (state.status === 'ready') {
    const r = state.result;
    const tone = marginTone(r.marginPct);
    const word = tone === 'positive' ? 'Strong' : tone === 'negative' ? 'Tight' : 'Healthy';
    const delta = r.perUnitSell - r.floorPerUnit;
    const floor = delta < 0 ? 'Below floor' : delta < 0.5 ? 'Close to floor' : 'Floor safe';
    return {
      primary: `${currency(r.perUnitSell)} / unit`,
      secondary: `${r.totalQty} × = ${currency(r.orderTotal)} · ${word} ${r.marginPct.toFixed(0)}% · ${floor}`,
    };
  }
  if (state.status === 'calculating') {
    return { primary: 'Calculating…', secondary: 'Tap for details' };
  }
  if (state.status === 'error') {
    return { primary: 'Error', secondary: 'Tap to review' };
  }
  const total = totalQty(values);
  return {
    primary: total === 0 ? 'Enter quantities' : `${total} units`,
    secondary: 'Tap for live pricing',
  };
}
